import React from 'react';

import { logErrorToMonitorSystem } from '../utils/errorLogging';

/**
 * Given a default value and key, will continuously update a value in `window.localStorage` of the same key.
 * @param defaultValue The default value to be provided for the `key`
 * @param key the name to be used when storing the value in local storage.
 */
export const usePersistState = <T,>(defaultValue: T, key) => {
  const [value, setValue] = React.useState<T>(() => {
    const persistedValue = window.localStorage.getItem(key);
    return persistedValue !== null ? JSON.parse(persistedValue) : defaultValue;
  });

  const prevValueRef = React.useRef<T>(value);

  React.useEffect(() => {
    try {
      if (JSON.stringify(value) !== JSON.stringify(prevValueRef.current)) {
        prevValueRef.current = value;
        window.localStorage.setItem(key, JSON.stringify(value));
      }
    } catch (error) {
      logErrorToMonitorSystem(`Error saving to localStorage: ${error}`);
    }
  }, [key, value]);

  return [value, setValue] as const;
};
